// Browser-safe frontmatter parser that extracts YAML frontmatter without Node Buffer.
function parseFrontmatter(raw) {
  const match = raw.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/);
  if (!match) {
    return { data: {}, content: raw };
  }

  const rawYaml = match[1];
  const content = match[2];
  const data = {};

  for (const line of rawYaml.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;
    const colonIdx = trimmed.indexOf(':');
    if (colonIdx === -1) continue;

    const key = trimmed.slice(0, colonIdx).trim();
    let val = trimmed.slice(colonIdx + 1).trim();

    if (
      (val.startsWith("'") && val.endsWith("'")) ||
      (val.startsWith('"') && val.endsWith('"'))
    ) {
      val = val.slice(1, -1);
    } else if (val.startsWith('[') && val.endsWith(']')) {
      val = val
        .slice(1, -1)
        .split(',')
        .map((item) => item.trim().replace(/^['"]|['"]$/g, ''))
        .filter(Boolean);
    } else if (val === 'true') {
      val = true;
    } else if (val === 'false') {
      val = false;
    }

    data[key] = val;
  }

  return { data, content };
}

// Vite loads every markdown file under src/content/blogs/ as raw text at
// build time. Supports both flat files (/src/content/blogs/*.md) and
// structured folders (/src/content/blogs/<slug>/main.md).
const rawFiles = import.meta.glob(
  [
    '/src/content/blogs/*.md',
    '/src/content/blogs/*/main.md',
    '/src/content/blogs/*/index.md',
  ],
  {
    eager: true,
    query: '?raw',
    import: 'default',
  },
);

function extractSlugAndBaseDir(filePath) {
  const parts = filePath.split('/');
  const fileName = parts.pop();
  if (fileName === 'main.md' || fileName === 'index.md') {
    const slug = parts[parts.length - 1];
    return {
      slug,
      baseDir: parts.join('/'),
      isFolder: true,
    };
  }
  const slug = fileName.replace(/\.md$/, '');
  return {
    slug,
    baseDir: parts.join('/'),
    isFolder: false,
  };
}

/**
 * @typedef {Object} BlogPost
 * @property {string} slug
 * @property {string} pathSlug
 * @property {string} baseDir
 * @property {string} filePath
 * @property {string} title
 * @property {string} author
 * @property {string} publishedDate  ISO date string, e.g. "2026-01-15"
 * @property {string} [lastEditedDate] ISO date string
 * @property {string[]} [tags]
 * @property {string} [summary]
 * @property {string} content  Markdown body, frontmatter stripped
 */

/** @returns {BlogPost[]} All blog posts, newest first. */
export function getAllBlogPosts() {
  const posts = Object.entries(rawFiles).map(([filePath, raw]) => {
    const { data, content } = parseFrontmatter(raw);
    const { slug: pathSlug, baseDir } = extractSlugAndBaseDir(filePath);
    const slug = data.slug || pathSlug;

    if (!data.title || !data.author || !data.publishedDate) {
      console.warn(
        `[blogs] "${filePath}" is missing required frontmatter ` +
          '(title, author, publishedDate). It will still render, but ' +
          'with placeholders.',
      );
    }

    return {
      slug,
      pathSlug,
      baseDir,
      filePath,
      title: data.title || 'Untitled post',
      author: data.author || 'YACC',
      publishedDate: data.publishedDate || null,
      lastEditedDate: data.lastEditedDate || null,
      tags: data.tags || [],
      summary: data.summary || '',
      content,
    };
  });

  return posts.sort((a, b) =>
    (b.publishedDate || '').localeCompare(a.publishedDate || ''),
  );
}

/** @returns {BlogPost | undefined} */
export function getBlogPostBySlug(slug) {
  if (!slug) return undefined;
  const posts = getAllBlogPosts();

  // 1. Direct match with frontmatter slug or path-derived slug
  const directMatch = posts.find(
    (post) => post.slug === slug || post.pathSlug === slug,
  );
  if (directMatch) return directMatch;

  // 2. Flexible match ignoring YYYY-MM-DD- prefix
  const cleanQuery = slug.replace(/^\d{4}-\d{2}-\d{2}-/, '');
  return posts.find((post) => {
    const cleanSlug = post.slug.replace(/^\d{4}-\d{2}-\d{2}-/, '');
    const cleanPathSlug = post.pathSlug.replace(/^\d{4}-\d{2}-\d{2}-/, '');
    return cleanSlug === cleanQuery || cleanPathSlug === cleanQuery;
  });
}
